import React, { useState, useCallback, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import { getClinicDetails } from "../../store/UserStore/Clinic/action";
import { getLocalStorage, resolvePincode } from "../../helpers/utils";
import ClinicList from "../../pages/clinics/ClinicList";

/**
 * EmbeddedClinicList
 *
 * Self-contained clinic listing with built-in location detection.
 * Drop into any page — no parent wiring needed.
 *
 * Props:
 *   serviceId       {string|null}  — optional UUID to pre-filter by service.
 *                                    Pass null (default) to show all clinics.
 *
 *   defaultLocation {object|null}  — optional { latitude, longitude } to skip
 *                                    the location permission flow entirely.
 *                                    Example: { latitude: 12.9716, longitude: 77.5946 }
 *
 *   previewMode     {boolean}      — when true, shows 4 clinics on desktop with
 *                                    a "Explore All Clinics Near You" button below,
 *                                    and hides the clinic list entirely on mobile
 *                                    (showing only the button).
 *
 * Layout behaviour (default, previewMode = false):
 *   Desktop (≥768px) — fixed 700px height, inner scroll, contained overscroll
 *   Mobile  (<768px) — natural height, flows into normal page scroll
 *
 * Layout behaviour (previewMode = true):
 *   Desktop (≥768px) — up to 4 clinic cards, no scroll, button below
 *   Mobile  (<768px) — clinic cards hidden, button only (full-width)
 */
const EmbeddedClinicList = ({
  serviceId = null,
  defaultLocation = null,
  previewMode = false,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const isLoggedIn = !!getLocalStorage("AUTH_DETAILS");
  const locationDetails = !!getLocalStorage("LOCATION_DETAILS");

  const [locationReady, setLocationReady] = useState(!!defaultLocation);
  const [locationDenied, setLocationDenied] = useState(false);
  const [locationError, setLocationError] = useState("");

  const [request, setRequest] = useState({
    page: 1,
    page_count: previewMode ? 4 : 9,
    keyword: "",
    service_id: serviceId || null,
    ...(defaultLocation
      ? { latitude: defaultLocation.latitude, longitude: defaultLocation.longitude }
      : {}),
  });

  // ── Location detection ──────────────────────────────────────────────────────
  const requestLocation = useCallback(
    (retry = 0) => {
      setLocationError("");

      if (!navigator.geolocation) {
        setLocationError("Geolocation is not supported by your browser.");
        return;
      }

      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          const { latitude, longitude } = pos.coords;
          await resolvePincode(latitude, longitude);
          setRequest((r) => ({ ...r, latitude, longitude }));
          setLocationReady(true);
          setLocationDenied(false);
        },
        (err) => {
          if (retry < 2) {
            setTimeout(() => requestLocation(retry + 1), 2000);
            return;
          }
          if (err.code === 1) {
            setLocationDenied(true);
          } else {
            setLocationError(
              "Unable to fetch location. Try again or move to an open area."
            );
          }
          setLocationReady(false);
        },
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
      );
    },
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(() => {
    if (defaultLocation) return;
    if (!isLoggedIn) {
      requestLocation();
    } else {
      setLocationReady(true);
    }
  }, [requestLocation, isLoggedIn, defaultLocation]);

  useEffect(() => {
    if (locationReady) {
      dispatch(getClinicDetails({ request }));
    }
  }, [request, locationReady]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Location prompt ─────────────────────────────────────────────────────────
  if (!defaultLocation && !locationReady && !isLoggedIn && !locationDetails) {
    // In preview mode on mobile, skip the location prompt and show the button
    return (
      <>
        <LocationPrompt previewMode={previewMode}>
          <LocationIcon>📍</LocationIcon>
          <h3>Location Required</h3>
          <p>
            We need your location to show clinics near you.
            <br />
            Please allow location access.
          </p>
          {locationError && <ErrorText>{locationError}</ErrorText>}
          <AllowButton onClick={() => requestLocation()}>
            Enable Location
          </AllowButton>
        </LocationPrompt>
        {previewMode && (
          <ViewAllWrapper>
            <ViewAllButton onClick={() => history.push("/clinic")}>
              Explore All Clinics Near You
            </ViewAllButton>
          </ViewAllWrapper>
        )}
      </>
    );
  }

  // ── Preview mode render ─────────────────────────────────────────────────────
  if (previewMode) {
    return (
      <ClinicSectionWrapper>
        {!defaultLocation && locationDenied && !isLoggedIn && (
          <LocationBanner>
            <p>Allow location access to see clinics near you.</p>
            <button onClick={() => requestLocation()}>Enable Location</button>
          </LocationBanner>
        )}
        {/* Desktop: show 4 cards. Mobile: hidden via CSS. */}
        <PreviewCardsWrapper>
          <ClinicList limit={4} />
        </PreviewCardsWrapper>
        {/* Button shown on all screen sizes */}
        <ViewAllWrapper>
          <ViewAllButton onClick={() => history.push("/clinic")}>
            Explore All Clinics Near You
          </ViewAllButton>
        </ViewAllWrapper>
      </ClinicSectionWrapper>
    );
  }

  // ── Default (full) render ───────────────────────────────────────────────────
  return (
    <ClinicSectionWrapper>
      {!defaultLocation && locationDenied && !isLoggedIn && (
        <LocationBanner>
          <p>Allow location access to see clinics near you.</p>
          <button onClick={() => requestLocation()}>Enable Location</button>
        </LocationBanner>
      )}
      <ScrollContainer>
        <ClinicList />
      </ScrollContainer>
    </ClinicSectionWrapper>
  );
};

export default EmbeddedClinicList;

// ─── Styled Components ────────────────────────────────────────────────────────

const ClinicSectionWrapper = styled.div`
  width: 100%;
  margin: 1.5rem 0;
`;

// ── Default mode: fixed-height scroll container ───────────────────────────────

const ScrollContainer = styled.div`
  @media (min-width: 768px) {
    height: 700px;
    overflow-y: auto;
    overscroll-behavior: contain;
    border: 1px solid #e8e8e8;
    border-radius: 12px;
    background: #ffffff;

    scrollbar-width: thin;
    scrollbar-color: #00419d #f0f0f0;

    &::-webkit-scrollbar {
      width: 6px;
    }
    &::-webkit-scrollbar-track {
      background: #f0f0f0;
      border-radius: 0 12px 12px 0;
    }
    &::-webkit-scrollbar-thumb {
      background: #00419d;
      border-radius: 3px;
    }
  }

  @media (max-width: 767px) {
    height: auto;
    overflow-y: visible;
  }
`;

// ── Preview mode: plain wrapper, cards hidden on mobile ───────────────────────

const PreviewCardsWrapper = styled.div`
  /* Kill the 65rem forced min-height from .main-content in care_profile.css */
  .main-content {
    min-height: 0 !important;
  }

  /* Desktop: show cards naturally */
  @media (min-width: 768px) {
    display: block;
  }

  /* Mobile: hide cards, only the button shows */
  @media (max-width: 767px) {
    display: none;
  }
`;

// ── "View all" button ─────────────────────────────────────────────────────────

const ViewAllWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 0.5rem;

  @media (max-width: 767px) {
    margin-top: 0;
  }
`;

const ViewAllButton = styled.button`
  background: #00419d;
  color: #ffffff;
  border: none;
  border-radius: 25px;
  padding: 14px 36px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  font-family: Montserrat, sans-serif;
  transition: background-color 0.2s ease;
  letter-spacing: 0.02em;

  &:hover {
    background: #003080;
  }

  @media (max-width: 767px) {
    width: 100%;
    border-radius: 10px;
    padding: 16px 24px;
    font-size: 16px;
  }
`;

// ── Location UI ───────────────────────────────────────────────────────────────

const LocationPrompt = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 2.5rem 1.5rem;
  border: 1px dashed #d0d0d0;
  border-radius: 12px;
  margin: 1.5rem 0;
  background: #fafafa;

  /* In preview mode on mobile, hide this prompt (only button shows) */
  ${({ previewMode }) =>
    previewMode &&
    `
    @media (max-width: 767px) {
      display: none;
    }
  `}

  h3 {
    font-size: 1.2rem;
    font-weight: 700;
    color: #1a1a2e;
    margin: 0.5rem 0;
    font-family: Montserrat, sans-serif;
  }

  p {
    font-size: 15px;
    color: #555;
    font-family: Montserrat, sans-serif;
    margin-bottom: 1rem;
    line-height: 1.6;
  }
`;

const LocationIcon = styled.div`
  font-size: 40px;
  margin-bottom: 0.5rem;
`;

const ErrorText = styled.p`
  color: #e53935 !important;
  font-size: 13px !important;
  margin-bottom: 0.75rem !important;
`;

const AllowButton = styled.button`
  background: #00419d;
  color: #ffffff;
  border: none;
  border-radius: 25px;
  padding: 12px 30px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  font-family: Montserrat, sans-serif;
  transition: background-color 0.2s ease;

  &:hover {
    background: #003080;
  }
`;

const LocationBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: #fff1e8;
  border: 1px solid #ffd5bf;
  border-radius: 8px;
  margin-bottom: 12px;

  p {
    margin: 0;
    color: #7a5d4a;
    font-size: 14px;
    font-family: Montserrat, sans-serif;
  }

  button {
    border: 0;
    padding: 8px 16px;
    border-radius: 8px;
    color: #ffffff;
    background: #cc5500;
    font-size: 13px;
    cursor: pointer;
    white-space: nowrap;
    margin-left: 12px;
  }
`;
